import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { LayoutDashboard, Calendar, Users, LogOut, Building2, CalendarDays, ClipboardList, Briefcase, DollarSign, FolderOpen, Bell, FileSpreadsheet, Sparkles, Store, BarChart2, Settings, UserCog, Activity, FilePen, Radio, UserPlus, CheckSquare, Target, FileText, Scale, Menu, X, ShieldCheck } from 'lucide-react'
import api from '../lib/api'
import { useEstablishment } from '../contexts/EstablishmentContext'
import { useModules } from '../lib/modules'

interface NavItem { to: string; icon: React.ReactNode; label: string; badge?: number; group: string }

interface NotifCounts { qseDepassees?: number; qseProches?: number; pendingAbsences: number; draftTimesheets: number; unscheduledCount: number; pendingSignatures: number; total: number }

const ROLE_RANK: Record<string, number> = {
  super_admin: 5, admin: 4, manager: 3, payroll: 2, viewer: 1
}
function hasRole(userRole: string, ...allowed: string[]) {
  return allowed.includes(userRole)
}

export default function Layout() {
  const navigate = useNavigate()
  const user = JSON.parse(localStorage.getItem('user') || '{}')
  const role: string = user.role || 'viewer'
  const isAdminUp  = hasRole(role, 'super_admin', 'admin')
  const isManager  = hasRole(role, 'super_admin', 'admin', 'manager')
  const isPayroll  = hasRole(role, 'super_admin', 'admin', 'payroll')
  const [counts, setCounts] = useState<NotifCounts>({ pendingAbsences: 0, draftTimesheets: 0, unscheduledCount: 0, pendingSignatures: 0, total: 0 })
  const [showNotif, setShowNotif] = useState(false)
  const { establishments, current, setCurrent } = useEstablishment()
  const { modules } = useModules()
  const rh = modules.rh
  const [menuOpen, setMenuOpen] = useState(false)
  const location = useLocation()
  useEffect(() => { setMenuOpen(false) }, [location.pathname])

  // Une seule liste de liens : barre d'icônes (ordinateur) ET menu du téléphone
  const S = 18
  const nav: NavItem[] = ([
    { to: '/dashboard',  icon: <LayoutDashboard size={S} />, label: 'Tableau de bord', group: 'Accueil', show: true },
    { to: '/planning',   icon: <Calendar size={S} />,        label: 'Planning', group: 'Planning & tâches', show: isManager },
    { to: '/absences',   icon: <CalendarDays size={S} />,    label: 'Absences', badge: counts.pendingAbsences, group: 'Planning & tâches', show: isManager && modules.conges },
    { to: '/timesheets', icon: <ClipboardList size={S} />,   label: 'Validation heures', badge: counts.draftTimesheets, group: 'Planning & tâches', show: isManager && rh },
    { to: '/clock-live', icon: <Radio size={S} />,           label: 'Badgeuse live', group: 'Planning & tâches', show: isManager && modules.badgeuse },
    { to: '/tasks',      icon: <CheckSquare size={S} />,     label: 'UtileClean', group: 'Planning & tâches', show: true },
    { to: '/missions',   icon: <Target size={S} />,          label: 'Missions', group: 'Planning & tâches', show: true },
    { to: '/bvp',        icon: <Scale size={S} />,           label: 'BVP', group: 'Planning & tâches', show: true },
    { to: '/job-sheets', icon: <ClipboardList size={S} />,   label: 'Fiches de poste', group: 'Planning & tâches', show: isManager },
    { to: '/qse',        icon: <ShieldCheck size={S} />,     label: 'Conformité QSE', badge: counts.qseDepassees || 0, group: 'Planning & tâches', show: isManager },
    { to: '/employees',  icon: <Users size={S} />,           label: 'Employés', group: 'Équipe', show: isManager },
    { to: '/onboarding', icon: <UserPlus size={S} />,        label: 'Nouvelle embauche', group: 'Équipe', show: isManager && rh },
    { to: '/positions',  icon: <Briefcase size={S} />,       label: 'Postes', group: 'Équipe', show: isManager },
    { to: '/contracts',  icon: <FilePen size={S} />,         label: 'Contrats & RH', group: 'Équipe', show: isManager && rh },
    { to: '/documents',  icon: <FolderOpen size={S} />,      label: 'Documents RH', group: 'Équipe', show: isManager && rh },
    { to: '/payslips',   icon: <FileText size={S} />,        label: 'Bulletins de paie', group: 'Équipe', show: isManager && rh },
    { to: '/payroll',    icon: <DollarSign size={S} />,      label: 'Rapport de paie', group: 'Finance', show: isPayroll && rh },
    { to: '/export',     icon: <FileSpreadsheet size={S} />, label: 'Export Comptabilité', group: 'Finance', show: isPayroll && rh },
    { to: '/reporting',  icon: <BarChart2 size={S} />,       label: 'Reporting', group: 'Finance', show: true },
    { to: '/assistant',  icon: <Sparkles size={S} />,        label: 'Assistant IA', group: 'Admin', show: modules.assistant },
    { to: '/establishments', icon: <Store size={S} />,       label: 'Établissements', group: 'Admin', show: isAdminUp },
    { to: '/users',      icon: <UserCog size={S} />,         label: 'Utilisateurs', group: 'Admin', show: isAdminUp },
    { to: '/sheets-sync', icon: <FileSpreadsheet size={S} />, label: 'Sync Google Sheets', group: 'Admin', show: isAdminUp && modules.planningAuto },
    { to: '/settings',   icon: <Settings size={S} />,        label: 'Paramètres', group: 'Admin', show: isAdminUp },
    { to: '/integration-logs', icon: <Activity size={S} />,  label: 'Logs intégration', group: 'Admin', show: isPayroll && rh },
  ] as (NavItem & { show: boolean })[]).filter(i => i.show)
  const groups = [...new Set(nav.map(i => i.group))]
  // Barre du bas (téléphone) : les 4 écrans du quotidien + Menu
  const BOTTOM = ['/dashboard', '/planning', '/tasks', '/missions']
  const bottom = BOTTOM.map(to => nav.find(i => i.to === to)).filter(Boolean) as NavItem[]

  useEffect(() => {
    // Demander permission notifications browser au premier chargement
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission()
    }
  }, [])

  useEffect(() => {
    let prevTotal = 0
    function load() {
      api.get('/notifications/counts').then(r => {
        const data = r.data as NotifCounts
        // Notification browser si nouveau total
        if (data.total > prevTotal && prevTotal > 0 && Notification.permission === 'granted') {
          new Notification('Planner Utile — Nouvelle alerte', {
            body: `${data.total} action${data.total > 1 ? 's' : ''} en attente`,
            icon: '/favicon.ico'
          })
        }
        prevTotal = data.total
        setCounts(data)
      }).catch(() => {})
    }
    load()
    const interval = setInterval(load, 60000)
    return () => clearInterval(interval)
  }, [])

  function logout() {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    navigate('/login')
  }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar compacte — icônes uniquement (ordinateur / tablette) */}
      <aside className="hidden md:flex w-14 bg-gray-900 flex-col flex-shrink-0">
        {/* Logo */}
        <div className="h-14 flex items-center justify-center border-b border-gray-700">
          <Building2 className="text-blue-400" size={22} />
        </div>

        {/* Nav */}
        <nav className="flex-1 flex flex-col items-center py-2 gap-0.5 overflow-y-auto" style={{ scrollbarWidth: 'none' }}>
          {groups.map((g, gi) => (
            <div key={g} className="contents">
              {gi > 0 && <div className="w-6 border-t border-gray-700 my-1" />}
              {nav.filter(i => i.group === g).map(i => <SideLink key={i.to} to={i.to} icon={i.icon} label={i.label} badge={i.badge} />)}
            </div>
          ))}
        </nav>

        {/* Logout */}
        <div className="flex flex-col items-center py-3 border-t border-gray-700">
          {/* Cloche notifications */}
          <div className="relative mb-1">
            <button
              onClick={() => setShowNotif(v => !v)}
              className="relative w-10 h-10 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-700 hover:text-white transition-colors">
              <Bell size={18} />
              {counts.total > 0 && (
                <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                  {counts.total > 9 ? '9+' : counts.total}
                </span>
              )}
            </button>
            {showNotif && (
              <div className="absolute left-full ml-3 bottom-0 w-64 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-semibold text-gray-900">Notifications</p>
                </div>
                <div className="divide-y divide-gray-50">
                  {((counts.qseDepassees || 0) + (counts.qseProches || 0)) > 0 && (
                    <button onClick={() => { setShowNotif(false); navigate('/qse') }} className="w-full text-left px-4 py-3 hover:bg-gray-50">
                      <p className="text-sm text-gray-800">
                        {(counts.qseDepassees || 0) > 0 && <><span className="font-semibold text-red-600">{counts.qseDepassees}</span> contrôle{(counts.qseDepassees || 0) > 1 ? 's' : ''} QSE dépassé{(counts.qseDepassees || 0) > 1 ? 's' : ''}{(counts.qseProches || 0) > 0 ? ' · ' : ''}</>}
                        {(counts.qseProches || 0) > 0 && <><span className="font-semibold text-amber-600">{counts.qseProches}</span> à prévoir sous 30 jours</>}
                      </p>
                    </button>
                  )}
                  {counts.pendingAbsences > 0 && (
                    <div className="px-4 py-3">
                      <p className="text-sm text-gray-800">
                        <span className="font-semibold text-orange-600">{counts.pendingAbsences}</span> absence{counts.pendingAbsences > 1 ? 's' : ''} en attente
                      </p>
                    </div>
                  )}
                  {counts.draftTimesheets > 0 && (
                    <div className="px-4 py-3">
                      <p className="text-sm text-gray-800">
                        <span className="font-semibold text-blue-600">{counts.draftTimesheets}</span> feuille{counts.draftTimesheets > 1 ? 's' : ''} à valider
                      </p>
                    </div>
                  )}
                  {counts.unscheduledCount > 0 && (
                    <div className="px-4 py-3">
                      <p className="text-sm text-gray-800">
                        <span className="font-semibold text-purple-600">{counts.unscheduledCount}</span> employé{counts.unscheduledCount > 1 ? 's' : ''} sans shift cette semaine
                      </p>
                    </div>
                  )}
                  {counts.pendingSignatures > 0 && (
                    <div className="px-4 py-3">
                      <p className="text-sm text-gray-800">
                        <span className="font-semibold text-indigo-600">{counts.pendingSignatures}</span> contrat{counts.pendingSignatures > 1 ? 's' : ''} en attente de signature
                      </p>
                    </div>
                  )}
                  {counts.total === 0 && (
                    <div className="px-4 py-4 text-center text-sm text-gray-400">Aucune alerte</div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="relative group">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold mb-2 cursor-default">
              {user.firstName?.[0]}{user.lastName?.[0]}
            </div>
            <div className="absolute left-full ml-3 top-0 bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
              {user.firstName} {user.lastName}<br />
              <span className="text-gray-400">{user.email}</span><br />
              <span className="text-blue-400 capitalize">{role.replace('_', ' ')}</span>
            </div>
          </div>
          <button
            onClick={logout}
            title="Déconnexion"
            className="relative group w-10 h-10 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-700 hover:text-white transition-colors">
            <LogOut size={18} />
            <span className="absolute left-full ml-3 bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
              Déconnexion
            </span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto flex flex-col">
        {/* Téléphone : barre du haut (magasin + alertes) */}
        <div className="md:hidden flex items-center gap-2 px-3 bg-gray-900 text-white flex-shrink-0"
          style={{ paddingTop: 'max(8px, env(safe-area-inset-top))', paddingBottom: '8px' }}>
          <Building2 className="text-blue-400 flex-shrink-0" size={20} />
          {establishments.length > 1 ? (
            <select value={current?.id || ''}
              onChange={e => { const est = establishments.find(x => x.id === e.target.value); if (est) setCurrent(est) }}
              className="flex-1 min-w-0 bg-gray-800 text-white text-sm font-semibold rounded-lg px-2 py-1.5 border border-gray-700">
              {establishments.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          ) : <span className="flex-1 text-sm font-semibold truncate">{current?.name || 'Planner Utile'}</span>}
          {counts.total > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold rounded-full px-2 py-0.5">{counts.total}</span>
          )}
        </div>

        {/* Bandeau établissement (multi-établissements, ordinateur) */}
        {establishments.length > 1 && (
          <div className="hidden md:flex items-center gap-3 px-5 py-2 bg-white border-b border-gray-100 flex-shrink-0">
            <span className="text-xs text-gray-500">Établissement :</span>
            <div className="flex gap-1">
              {establishments.map(e => (
                <button
                  key={e.id}
                  onClick={() => setCurrent(e)}
                  className={`text-xs px-3 py-1 rounded-full font-medium transition-colors ${
                    current?.id === e.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}>
                  {e.name}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="flex-1 overflow-auto pb-16 md:pb-0">
          <Outlet />
        </div>

        {/* Téléphone : barre du bas */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 flex"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
          {bottom.map(i => (
            <NavLink key={i.to} to={i.to}
              className={({ isActive }) => `flex-1 flex flex-col items-center gap-0.5 py-2 text-[11px] ${isActive ? 'text-blue-600' : 'text-gray-500'}`}>
              {i.icon}
              <span className="truncate max-w-full px-1">{i.label === 'Tableau de bord' ? 'Accueil' : i.label === 'UtileClean' ? 'Tâches' : i.label}</span>
            </NavLink>
          ))}
          <button onClick={() => setMenuOpen(true)} className="flex-1 flex flex-col items-center gap-0.5 py-2 text-[11px] text-gray-500">
            <Menu size={18} />
            <span>Menu</span>
          </button>
        </nav>

        {/* Téléphone : menu complet */}
        {menuOpen && (
          <div className="md:hidden fixed inset-0 z-50 bg-black/40" onClick={() => setMenuOpen(false)}>
            <div className="absolute bottom-0 left-0 right-0 max-h-[85vh] overflow-y-auto bg-white rounded-t-2xl"
              style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
              onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 sticky top-0 bg-white">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{user.firstName} {user.lastName}</p>
                  <p className="text-xs text-gray-400">{current?.name}</p>
                </div>
                <button onClick={() => setMenuOpen(false)} className="p-2 text-gray-400"><X size={20} /></button>
              </div>
              {groups.map(g => (
                <div key={g} className="py-1">
                  <p className="px-5 pt-2 pb-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">{g}</p>
                  {nav.filter(i => i.group === g).map(i => (
                    <NavLink key={i.to} to={i.to}
                      className={({ isActive }) => `flex items-center gap-3 px-5 py-3 text-sm ${isActive ? 'text-blue-600 bg-blue-50' : 'text-gray-700'}`}>
                      {i.icon}
                      <span className="flex-1">{i.label}</span>
                      {i.badge ? <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5">{i.badge}</span> : null}
                    </NavLink>
                  ))}
                </div>
              ))}
              <button onClick={logout} className="w-full flex items-center gap-3 px-5 py-3 text-sm text-red-600 border-t border-gray-100">
                <LogOut size={18} /> Déconnexion
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}

function SideLink({ to, icon, label, badge }: { to: string; icon: React.ReactNode; label: string; badge?: number }) {
  return (
    <NavLink
      to={to}
      title={label}
      className={({ isActive }) =>
        `relative group w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
          isActive ? 'bg-blue-600 text-white' : 'text-gray-400 hover:bg-gray-700 hover:text-white'
        }`
      }>
      {icon}
      {badge != null && badge > 0 && (
        <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none">
          {badge > 9 ? '9+' : badge}
        </span>
      )}
      <span className="absolute left-full ml-3 bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
        {label}
      </span>
    </NavLink>
  )
}
